import { Box, FormHelperText, InputAdornment, TextField, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ILabeledInputProps {
  label: string;
  id: string;
  type: string;
  value?: string | number;
  placeholder?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  handleChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  width?: string;
  required?: boolean;
  name?: string;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  disabled?: boolean;
  backgroundColor?: string;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  maxLength?: number;
  fullWidth?: boolean;
}
const StyledTextField = styled(TextField)(({ theme, multiline, error, sx, disabled }) => ({
  '& .MuiOutlinedInput-root': {
    height: !multiline && '48px',
    borderRadius: '10px',
    marginTop: '4px',
    backgroundColor: disabled ? '#EEE' : '#FFF',
    '& fieldset': {
      padding: '12px',
      color: theme.palette.primary.main,
      border:
        (error && '1px solid var(--Alert-Sunset-Orange, #FF9A9A)') ||
        (disabled && '1px solid #EEE !important') ||
        '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&:hover fieldset': {
      border:
        (error && '1px solid var(--Alert-Sunset-Orange, #FF9A9A)') ||
        (disabled && '1px solid #EEE') ||
        '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&.Mui-focused fieldset': {
      border:
        (error && '1px solid var(--Alert-Sunset-Orange, #FF9A9A)') ||
        (disabled && '1px solid #EEE') ||
        '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
  },
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: '0px',
    right: '0px',
  },
  '& ::placeholder': {
    opacity: 0.2,
  },
  '& .MuiOutlinedInput-input.Mui-disabled': {
    '-webkit-text-fill-color': theme.palette.primary.main,
    opacity: 0.6,
  },
  sx,
}));

const LabeledInput = ({
  label,
  id,
  type,
  placeholder,
  endIcon,
  handleChange,
  value,
  width,
  required,
  name,
  onBlur,
  error,
  multiline,
  startIcon,
  minRows,
  maxRows,
  disabled,
  backgroundColor,
  fontSize,
  fontWeight,
  color,
  maxLength,
  fullWidth = false,
}: ILabeledInputProps) => {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" justifyContent="center">
      <Typography variant="meta" color="primary">
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>
      <StyledTextField
        id={id}
        label=""
        value={value}
        placeholder={placeholder}
        type={type}
        variant="outlined"
        onChange={handleChange}
        InputProps={{
          endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : null,
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : null,
          inputProps: {
            maxLength, // Set maxLength
          },
        }}
        style={{ width }}
        required={required}
        name={name}
        onBlur={onBlur}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        error={!!error}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={{
          backgroundColor,
          '& .MuiOutlinedInput-root': {
            fontSize,
            fontWeight,
            color,
          },
        }}
      />
      {error && <FormHelperText sx={{ color: 'red' }}>{t(String(error))}</FormHelperText>}
      {maxLength && (
        <Typography variant="caption" color="textSecondary" align="right">
          {value?.toString().length}/{maxLength} characters
        </Typography>
      )}
    </Box>
  );
};

LabeledInput.defaultProps = {
  placeholder: '',
  endIcon: null,
  value: '',
  handleChange: () => {},
  required: false,
  maxLength: undefined,
};

export default LabeledInput;
